import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ProdutoService } from '../services/produto.service';
import { EmpresasService } from '../superadmin/services/empresas.service';
import { FiscalService } from '../fidelidade/fiscal/services/fiscal.service';
import {
  ChoiceChargeTypeV2,
  ChoiceGroupTypeV2,
  ChoiceGroupVM,
  ChoiceOptionVM,
  LibraryGroupVM,
  PizzaAdditionalKindV2,
  PizzaAdditionalVM,
  PizzaOptionVM,
  ProductAvailabilityVM,
  ProductFiscalCestOptionVM,
  ProductCatalogItemVM,
  ProductEditorVM,
  ProductStatusV2,
  ProductTypeV2
} from './produtos-v2.models';
import { Produto } from '../../../server/domain/Produto';
import { ProdutoPizza } from '../../../server/domain/ProdutoPizza';
import { ProdutoBrindeFidelidade } from '../../../server/domain/ProdutoBrindeFidelidade';
import { ProdutoTamanho } from '../../../server/domain/templates/ProdutoTamanho';
import { AdicionalDeProdutoEscolhaSimples } from '../../../server/domain/delivery/AdicionalDeProdutoEscolhaSimples';
import { AdicionalDeProdutoMultiplaEscolha } from '../../../server/domain/delivery/AdicionalDeProdutoMultiplaEscolha';
import { OpcaoDeAdicionalDeProduto } from '../../../server/domain/delivery/OpcaoDeAdicionalDeProduto';
import { EnumTipoDeCobrancaDeAdicional } from '../../../server/lib/emun/EnumTipoDeCobrancaDeAdicional';

@Injectable()
export class ProdutosV2FacadeService {
  private libraryProductsCache = new Map<number, any[]>();

  constructor(
    private produtoService: ProdutoService,
    private empresasService: EmpresasService,
    private fiscalService: FiscalService,
    private http: HttpClient
  ) {}

  async carregueCatalogo(empresa: any): Promise<ProductCatalogItemVM[]> {
    const catalogo = empresa?.catalogo;
    if (!catalogo?.id) return [];

    const [ativosResp, inativosResp] = await Promise.all([
      this.produtoService.liste(catalogo.id, true, 0, 400),
      this.produtoService.liste(catalogo.id, false, 0, 400)
    ]);

    const itensAtivos = ativosResp?.produtos || [];
    const itensInativos = inativosResp?.produtos || [];
    const mapa = new Map<number, ProductCatalogItemVM>();

    itensAtivos.forEach((produto: any) => {
      mapa.set(produto.id, this.mapCatalogItem(produto, 'active'));
    });

    itensInativos.forEach((produto: any) => {
      if (!mapa.has(produto.id)) {
        mapa.set(produto.id, this.mapCatalogItem(produto, 'draft'));
      }
    });

    const items = Array.from(mapa.values()).sort((a, b) => a.name.localeCompare(b.name));
    this.libraryProductsCache.set(catalogo.id, items.map((item) => item.raw));
    return items;
  }

  async carregueProduto(empresa: any, idProduto: number, templates: any[] = []): Promise<ProductEditorVM> {
    const produto = await this.produtoService.obtenhaProduto(idProduto, empresa.catalogo, empresa);
    return this.mapEditor(produto, templates);
  }

  async carregueTags(): Promise<any[]> {
    return this.produtoService.listeTagsAlimentar();
  }

  async carregueUnidades(): Promise<any[]> {
    return this.produtoService.listeUnidades();
  }

  async carregueDadosFiscais(): Promise<{ origens: any[]; tiposTributacaoIPI: any[]; selosControleIPI: any[] }> {
    const [origens, tiposTributacaoIPI, selosControleIPI] = await Promise.all([
      this.fiscalService.listeOrigens(),
      this.fiscalService.listeTiposDeTributacaoIPI(),
      this.fiscalService.listeSelosDeControleIPI()
    ]);
    return { origens: origens || [], tiposTributacaoIPI: tiposTributacaoIPI || [], selosControleIPI: selosControleIPI || [] };
  }

  async busqueNcms(query: string = '', codigo: string = '', limit: number = 80): Promise<any[]> {
    return this.fiscalService.listeNcms(query, codigo, limit);
  }

  async busqueCests(ncm: string = '', query: string = '', codigo: string = '', limit: number = 80): Promise<ProductFiscalCestOptionVM[]> {
    return this.fiscalService.listeCests(ncm, query, codigo, limit);
  }

  async salveFiscal(produto: any): Promise<void> {
    await this.fiscalService.salveConfiguracoesFiscais(produto, produto.configuracaoFiscal);
  }

  async salvePontosFidelidade(produto: any): Promise<any> {
    return this.produtoService.salvePontosProduto(produto);
  }

  async carregueTemplates(catalogo: any): Promise<any[]> {
    if (!catalogo?.id) return [];
    return this.empresasService.obtenhaTemplates(catalogo);
  }

  crieProdutoNovo(tipo: ProductTypeV2, catalogo: any, templates: any[] = []): Produto {
    if (tipo === 'pizza') {
      const template = templates[0];
      const pizza = new ProdutoPizza();
      pizza.catalogo = catalogo;
      pizza.template = template || null;
      pizza.categoria = catalogo?.categorias?.[0] || null;
      pizza.disponibilidades = [];

      if (template?.tamanhos?.length) {
        pizza.tamanhos = template.tamanhos.map((tamanhoTemplate: any) => (
          new ProdutoTamanho(null, 0, tamanhoTemplate, true)
        ));
      }

      return pizza;
    }

    if (tipo === 'brindefidelidade') {
      const produtoBrinde = new ProdutoBrindeFidelidade();
      produtoBrinde.catalogo = catalogo;
      produtoBrinde.categoria = catalogo?.categorias?.[0] || null;
      produtoBrinde.disponibilidades = [];
      return produtoBrinde;
    }

    const produto = new Produto();
    produto.catalogo = catalogo;
    produto.categoria = catalogo?.categorias?.[0] || null;
    produto.disponibilidades = [];
    return produto;
  }

  async salveProduto(produto: any): Promise<any> {
    const resposta = await this.produtoService.salveProduto(produto);
    if (!produto.id && resposta?.id) {
      produto.id = resposta.id;
    }
    if (resposta?.tamanhos) {
      produto.tamanhos = resposta.tamanhos;
    }
    if (resposta?.ordem != null) {
      produto.ordem = resposta.ordem;
    }
    return resposta;
  }

  apliquePrecoEfetivo(produto: any, valor: number): void {
    const amount = Number(valor ?? 0);
    const priceTarget = this.resolveEffectivePriceTarget(produto);

    if (priceTarget === 'company-promo') {
      const produtoNaEmpresa = this.ensureProdutoNaEmpresa(produto);
      produtoNaEmpresa.novoPreco = amount;
      produto.novoPrecoNaEmpresa = amount;
      return;
    }

    if (priceTarget === 'global-promo') {
      produto.novoPreco = amount;
      return;
    }

    if (priceTarget === 'company-base') {
      const produtoNaEmpresa = this.ensureProdutoNaEmpresa(produto);
      produtoNaEmpresa.preco = amount;
      produto.precoNaEmpresa = amount;
      return;
    }

    produto.preco = amount;
  }

  apliqueDisponibilidade(produto: any, availability: ProductAvailabilityVM): void {
    const disponibilidade = availability.disponibilidadeTipo != null ? availability.disponibilidadeTipo : (availability.active ? 0 : 2);

    if (this.shouldUseCompanyAvailability(produto)) {
      const produtoNaEmpresa = this.ensureProdutoNaEmpresa(produto);
      produtoNaEmpresa.disponibilidade = disponibilidade;
      produto.disponibilidadeNaEmpresa = disponibilidade;
    } else {
      delete produto.disponibilidadeNaEmpresa;
      produto.disponibilidade = disponibilidade;
    }

    produto.disponivelParaDelivery = availability.delivery;
    produto.disponivelNaMesa = availability.balcao;
    produto.exibirNoSite = availability.exibirNoSite;
    produto.exibirPrecoSite = availability.exibirPrecoSite;
    produto.exibirPrecoNoCardapio = availability.exibirPrecoNoCardapio;
    produto.mensagemPedido = availability.mensagemPedido;
    produto.naoSincronizar = availability.naoSincronizar;
  }

  apliqueDestaque(produto: any, destaque: boolean): void {
    if (this.shouldUseCompanyScopedDestaque(produto)) {
      const produtoNaEmpresa = this.ensureProdutoNaEmpresa(produto);
      produtoNaEmpresa.destaque = destaque;
      produto.destaqueNaEmpresa = destaque;
      return;
    }

    produto.destaque = destaque;
  }

  async salvePrecoRapido(empresa: any, item: ProductCatalogItemVM, novoPreco: number): Promise<void> {
    const produto = await this.produtoService.obtenhaProduto(item.id, empresa.catalogo, empresa);
    this.apliquePrecoEfetivo(produto, novoPreco);
    await this.salveProduto(produto);
    item.raw = produto;
    item.basePrice = novoPreco;
  }

  async dupliqueProduto(empresa: any, item: ProductCatalogItemVM): Promise<any> {
    return this.produtoService.cloneProduto(item.id, empresa.catalogo);
  }

  async removaProduto(item: ProductCatalogItemVM): Promise<void> {
    await this.produtoService.removaProduto(item.raw);
  }

  async altereStatusProduto(empresa: any, item: ProductCatalogItemVM, ativo: boolean): Promise<void> {
    const produto = await this.produtoService.obtenhaProduto(item.id, empresa.catalogo, empresa);
    this.apliqueDisponibilidade(produto, {
      active: ativo,
      delivery: produto.disponivelParaDelivery !== false,
      balcao: produto.disponivelNaMesa !== false,
      disponibilidadeTipo: ativo ? 0 : 2,
      exibirNoSite: produto.exibirNoSite !== false,
      exibirPrecoSite: produto.exibirPrecoSite !== false,
      exibirPrecoNoCardapio: produto.exibirPrecoNoCardapio !== false,
      mensagemPedido: produto.mensagemPedido || '',
      naoSincronizar: !!produto.naoSincronizar
    });
    await this.produtoService.salveDisponibilidadeProduto(empresa.catalogo, produto);
  }

  async altereCategoriaProduto(empresa: any, item: ProductCatalogItemVM, categoria: any): Promise<void> {
    const produto = await this.produtoService.obtenhaProduto(item.id, empresa.catalogo, empresa);
    produto.categoria = categoria;
    await this.salveProduto(produto);
  }

  async carregueBiblioteca(catalogo: any, texto: string = '', produtoAtual?: any): Promise<LibraryGroupVM[]> {
    if (!catalogo?.id) return [];

    const normalizedText = String(texto || '').trim();
    const products = await this.carregueProdutosDaBiblioteca(catalogo, normalizedText);
    const currentProduct = produtoAtual?.raw || produtoAtual || null;
    const currentProductId = currentProduct?.id ?? produtoAtual?.id ?? produtoAtual ?? null;
    const existingAdditionalIds = new Set(
      (currentProduct?.camposAdicionais || [])
        .filter((adicional: any) => adicional?.id && !adicional?.removida)
        .map((adicional: any) => String(adicional.id))
    );
    const libraryMap = new Map<string, LibraryGroupVM>();

    products.forEach((produto) => {
      if (currentProductId != null && String(produto?.id ?? '') === String(currentProductId)) {
        return;
      }

      (produto?.camposAdicionais || []).forEach((adicional: any) => {
        if (!adicional?.id || adicional?.removida) {
          return;
        }

        const key = String(adicional.id);
        if (existingAdditionalIds.has(key)) {
          return;
        }

        const existing = libraryMap.get(key);
        if (existing) {
          existing.sharedCount += 1;
          existing.shared = existing.sharedCount > 1;
          existing.raw.sharedCount = existing.sharedCount;
          existing.raw.qtdeProdutos = existing.sharedCount;
          existing.raw.qtdProdutos = existing.sharedCount;
          existing.helperText = existing.sharedCount > 1
            ? 'Compartilhado — editar afeta todos'
            : 'Disponível para reutilizar neste catálogo';
          existing.usageLabel = `${existing.description} • ${existing.sharedCount} produto${existing.sharedCount > 1 ? 's' : ''}`;
          if (produto?.nome && !(existing.sharedProductNames || []).includes(produto.nome)) {
            existing.sharedProductNames = existing.sharedProductNames || [];
            existing.sharedProductNames.push(produto.nome);
          }
          return;
        }

        libraryMap.set(key, this.mapLibraryItemFromProduct(adicional, produto));
      });
    });

    return Array.from(libraryMap.values())
      .filter((item) => !normalizedText || item.name.toLowerCase().includes(normalizedText.toLowerCase()))
      .sort((left, right) => right.sharedCount - left.sharedCount || left.name.localeCompare(right.name, 'pt-BR'));
  }

  async vinculeGrupoDaBiblioteca(catalogo: any, produto: any, grupo: LibraryGroupVM, clonar: boolean): Promise<any> {
    const origemProduto = grupo.raw?.produto
      || grupo.raw?.objeto
      || grupo.raw?.item
      || grupo.raw?.produtoOrigem
      || (grupo.productId ? { id: grupo.productId } : {});
    return this.produtoService.copieAdicionais(catalogo, produto, origemProduto, [grupo.raw], clonar);
  }

  async salveDisponibilidade(catalogo: any, produto: any): Promise<void> {
    await this.produtoService.salveDisponibilidadeProduto(catalogo, produto);
  }

  async salveTags(catalogo: any, produto: any, tags: any[]): Promise<void> {
    await this.produtoService.salveTagsProdutos(catalogo, produto, tags);
  }

  async reordeneGrupos(catalogo: any, produto: any, grupos: ChoiceGroupVM[]): Promise<void> {
    const adicionais = grupos
      .filter((grupo) => grupo.kind === 'adicional' && !!grupo.raw)
      .map((grupo, index) => {
        grupo.order = index;
        grupo.raw.ordem = index;
        return grupo.raw;
      });

    if (!adicionais.length) {
      return;
    }

    await this.produtoService.atualizeOrdemAdicionais(catalogo, produto, adicionais);
  }

  async salveGrupo(catalogo: any, produto: any, grupo: ChoiceGroupVM): Promise<any> {
    if (grupo.kind === 'pizza-sizes') {
      return this.salveProduto(produto);
    }

    const adicional = this.mapGrupoParaLegacy(grupo);

    if (!produto.id) {
      grupo.raw = adicional;
      return adicional;
    }

    const resposta = await this.produtoService.salveAdicional(catalogo, produto, adicional);

    if (!adicional.id && resposta?.id) {
      adicional.id = resposta.id;
      grupo.id = resposta.id;
    }

    grupo.raw = adicional;
    return resposta;
  }

  async removaGrupo(produto: any, catalogo: any, grupo: ChoiceGroupVM): Promise<void> {
    if (grupo.kind === 'pizza-sizes') return;
    await this.produtoService.removaAdicional(produto, grupo.raw, catalogo.id);
  }

  async salveOpcao(catalogo: any, produto: any, grupo: ChoiceGroupVM, opcao: ChoiceOptionVM): Promise<any> {
    if (grupo.kind === 'pizza-sizes') {
      return this.salveProduto(produto);
    }

    const legacyGroup = grupo.raw || this.mapGrupoParaLegacy(grupo);
    const legacyOption = this.mapOpcaoParaLegacy(opcao);
    const resposta = await this.produtoService.salveOpcaoAdicional(catalogo, produto, legacyGroup, legacyOption);

    if (!opcao.id && resposta?.id) {
      opcao.id = resposta.id;
    }

    opcao.raw = legacyOption;
    grupo.raw = legacyGroup;
    return resposta;
  }

  mapEditor(produto: any, templates: any[] = []): ProductEditorVM {
    const status = this.getStatus(produto);
    const imageCandidates = this.resolveImageCandidates(produto);
    const image = imageCandidates[0] || null;
    const groups = this.mapGroups(produto);
    const tags = (produto?.tags || []).map((tag: any) => tag.nome);

    const isPizza = produto.tipo === 'pizza';
    let pizzaMassa: PizzaAdditionalVM | null = null;
    let pizzaBorda: PizzaAdditionalVM | null = null;

    if (isPizza && templates.length) {
      const pizzaAdditionals = this.buildPizzaAdditionals(produto, templates);
      pizzaMassa = pizzaAdditionals.massa;
      pizzaBorda = pizzaAdditionals.borda;
    }

    return {
      id: produto.id || null,
      name: produto.nome || '',
      category: produto?.categoria?.nome || 'Sem categoria',
      categoryId: produto?.categoria?.id || null,
      basePrice: this.getBasePrice(produto),
      description: produto.descricao || '',
      status,
      image,
      imageCandidates,
      imageCandidateIndex: image ? 0 : -1,
      imageMode: image ? 'image' : 'emoji',
      emoji: this.resolvePlaceholder(produto),
      type: (produto.tipo || 'normal') as ProductTypeV2,
      codigoPdv: produto.codigoPdv || '',
      tipoDeVenda: produto.tipoDeVenda || 'Unidade',
      incremento: produto.incremento ?? null,
      qtdeMinima: produto.qtdeMinima ?? 1,
      qtdMaxima: produto.qtdMaxima ?? 0,
      pesoMinimo: produto.pesoMinimo ?? 0,
      pesoMaximo: produto.pesoMaximo ?? 0,
      unidadeMedida: produto.unidadeMedida || null,
      valorInicial: produto.valorInicial ?? null,
      ehBrinde: !!produto.ehBrinde,
      naoAceitaCupom: !!produto.naoAceitaCupom,
      sincronizarModelos: !!produto.sincronizarModelos,
      promotion: {
        novoPreco: this.toNullableNumber(produto.novoPreco),
        percentualDesconto: this.toNullableNumber(produto.percentualDesconto),
        novoPrecoNaEmpresa: this.toNullableNumber(produto?.produtoNaEmpresa?.novoPreco ?? produto?.novoPrecoNaEmpresa),
        percentualDescontoNaEmpresa: this.toNullableNumber(produto.percentualDescontoNaEmpresa),
        exibirDescontos: produto.novoPreco != null || !!(produto?.catalogo?.precoPorEmpresa && produto?.novoPrecoNaEmpresa != null)
      },
      fiscal: this.mapFiscal(produto),
      fidelidade: {
        pontosGanhos: produto.pontosGanhos ?? null,
        cashback: produto.cashback ?? null
      },
      groups,
      pizzaMassa,
      pizzaBorda,
      availability: {
        active: status === 'active',
        delivery: produto.disponivelParaDelivery !== false,
        balcao: produto.disponivelNaMesa !== false,
        disponibilidadeTipo: this.getDisponibilidade(produto) ?? 0,
        exibirNoSite: produto.exibirNoSite !== false,
        exibirPrecoSite: produto.exibirPrecoSite !== false,
        exibirPrecoNoCardapio: produto.exibirPrecoNoCardapio !== false,
        mensagemPedido: produto.mensagemPedido || '',
        naoSincronizar: !!produto.naoSincronizar
      },
      classification: {
        destaque: this.getDestaque(produto),
        novidade: !!produto.novidade,
        tags
      },
      raw: produto
    };
  }

  buildPizzaAdditionals(produto: any, templates: any[]): { massa: PizzaAdditionalVM | null; borda: PizzaAdditionalVM | null } {
    if (!produto.template) return { massa: null, borda: null };

    const fullTemplate = templates.find((t: any) => t.id === produto.template.id);
    if (!fullTemplate?.adicionais?.length) return { massa: null, borda: null };

    const massa = this.buildPizzaAdditional('massa', 'Massa', produto, fullTemplate);
    const borda = this.buildPizzaAdditional('borda', 'Borda', produto, fullTemplate);

    return { massa, borda };
  }

  private buildPizzaAdditional(kind: PizzaAdditionalKindV2, descricao: string, produto: any, fullTemplate: any): PizzaAdditionalVM | null {
    const templateAdicionais = (fullTemplate.adicionais || []).filter(
      (a: any) => a.descricao?.toLowerCase() === descricao.toLowerCase()
    );

    if (!templateAdicionais.length) return null;

    const adicionalTemplate = templateAdicionais[0];

    let adicionalRaw = (produto.camposAdicionais || []).find(
      (a: any) => a.template && a.template.id === adicionalTemplate.id
    );

    if (!adicionalRaw) {
      adicionalRaw = new AdicionalDeProdutoEscolhaSimples(adicionalTemplate.descricao, true);
      adicionalRaw.template = adicionalTemplate;
    }

    (adicionalTemplate.opcoes || []).forEach((opcaoTemplate: any) => {
      const existe = (adicionalRaw.opcoesDisponiveis || []).find(
        (opcao: any) => opcao.template && opcaoTemplate.id === opcao.template.id
      );

      if (!existe) {
        const novaOpcao = new OpcaoDeAdicionalDeProduto(
          opcaoTemplate.nome,
          opcaoTemplate.valor,
          false,
          opcaoTemplate.descricao,
          opcaoTemplate
        );
        novaOpcao.codigoPdv = opcaoTemplate.codigoPdv;
        if (!adicionalRaw.opcoesDisponiveis) adicionalRaw.opcoesDisponiveis = [];
        adicionalRaw.opcoesDisponiveis.push(novaOpcao);
      }
    });

    const options: PizzaOptionVM[] = (adicionalRaw.opcoesDisponiveis || []).map((opcao: any) => ({
      id: opcao.id || null,
      name: opcao.nome || '',
      price: opcao.valor ?? 0,
      codigoPdv: opcao.codigoPdv || '',
      sizeName: opcao.template?.tamanho?.descricao || null,
      active: opcao.disponivel !== false,
      semBorda: !!opcao.template?.semborda,
      raw: opcao
    }));

    return {
      kind,
      name: descricao,
      saving: false,
      dirty: false,
      options,
      raw: adicionalRaw
    };
  }

  async salvePizzaAdicional(catalogo: any, produto: any, adicionalVM: PizzaAdditionalVM, empresa: any): Promise<void> {
    adicionalVM.saving = true;

    try {
      adicionalVM.options.forEach((opt) => {
        opt.raw.disponivel = opt.active;
        opt.raw.valor = opt.price;
        opt.raw.codigoPdv = opt.codigoPdv;
      });

      const rede = empresa?.id ? null : true;
      const resposta = await this.produtoService.salveAdicional(catalogo, produto, adicionalVM.raw, rede);

      if (!adicionalVM.raw.id && resposta?.id) {
        adicionalVM.raw.id = resposta.id;
      }
      if (resposta?.opcoesDisponiveis) {
        adicionalVM.raw.opcoesDisponiveis = resposta.opcoesDisponiveis;
      }

      adicionalVM.dirty = false;
    } finally {
      adicionalVM.saving = false;
    }
  }

  private mapCatalogItem(produto: any, status: ProductStatusV2): ProductCatalogItemVM {
    const imageCandidates = this.resolveImageCandidates(produto);
    const image = imageCandidates[0] || null;
    const placeholderToken = this.resolvePlaceholder(produto);
    const hasStartingPrice = this.hasStartingPrice(produto);
    return {
      id: produto.id,
      name: produto.nome,
      category: produto?.categoria?.nome || 'Sem categoria',
      categoryId: produto?.categoria?.id || null,
      description: produto?.descricao || '',
      basePrice: this.getBasePrice(produto),
      hasStartingPrice,
      status,
      image,
      imageCandidates,
      imageCandidateIndex: image ? 0 : -1,
      placeholderToken,
      hasValidImage: !!image,
      imageMode: image ? 'image' : 'emoji',
      emoji: placeholderToken,
      groupCount: this.getGroupCount(produto),
      type: (produto.tipo || 'normal') as ProductTypeV2,
      ordem_exibicao: produto.ordem ?? 0,
      raw: produto
    };
  }

  private mapGroups(produto: any): ChoiceGroupVM[] {
    const grupos: ChoiceGroupVM[] = [];

    if (produto?.tipo === 'pizza' && produto?.tamanhos?.length) {
      grupos.push({
        id: 'pizza-sizes',
        name: 'Tamanhos',
        type: 'single',
        required: true,
        min: 1,
        max: 1,
        chargeType: 'maior',
        shared: false,
        sharedCount: 0,
        order: -1,
        kind: 'pizza-sizes',
        description: `Escolha 1 • ${produto.tamanhos.length} opções`,
        options: produto.tamanhos.map((tamanho: any, index: number) => ({
          id: tamanho.id || `size-${index}`,
          name: tamanho.descricao || tamanho?.template?.descricao || `Tamanho ${index + 1}`,
          price: this.getBasePrice(tamanho),
          active: tamanho.disponivel !== false,
          order: index,
          codigoPdv: tamanho.codigoPdv || '',
          novoPreco: this.toNullableNumber(tamanho.novoPreco),
          percentualDesconto: this.toNullableNumber(tamanho.percentualDesconto),
          destaque: !!tamanho.destaque,
          raw: tamanho
        })),
        raw: produto.tamanhos
      });
    }

    (produto?.camposAdicionais || []).forEach((adicional: any, index: number) => {
      grupos.push({
        id: adicional.id || `group-${index}`,
        name: adicional.nome || 'Grupo',
        type: this.mapTipoGrupo(adicional),
        multiplicaQuantidadeProduto: adicional?.multiplicaQuantidadeProduto !== false,
        required: !!adicional.obrigatorio,
        min: adicional.qtdMinima || (adicional.obrigatorio ? 1 : 0),
        max: adicional.qtdMaxima || 1,
        chargeType: this.mapTipoCobranca(adicional.tipoDeCobranca),
        shared: !!adicional.compartilhado,
        sharedCount: adicional.sharedCount || adicional.qtdeProdutos || adicional.qtdProdutos || 0,
        order: adicional.getOrdem ? adicional.getOrdem() : (adicional.ordem ?? index),
        kind: 'adicional',
        description: this.descrevaGrupo(adicional),
        options: (adicional.opcoesDisponiveis || []).filter((opcao: any) => !opcao.removida).map((opcao: any, optionIndex: number) => ({
          id: opcao.id || `option-${optionIndex}`,
          name: opcao.nome || 'Opção',
          description: opcao.descricao || '',
          linkImagem: opcao.linkImagem || null,
          qtdeMaxima: opcao.qtdeMaxima ?? null,
          price: this.getOptionPrice(produto, opcao),
          active: this.getOptionAvailability(produto, opcao),
          order: optionIndex,
          raw: opcao
        })),
        raw: adicional
      });
    });

    return grupos.sort((a, b) => a.order - b.order);
  }

  private mapTipoGrupo(adicional: any): ChoiceGroupTypeV2 {
    if (adicional?.tipo === 'escolha-simples') return 'single';
    if (adicional?.podeRepetirItem) return 'multipleRepeat';
    return 'multiple';
  }

  private mapTipoCobranca(tipo: any): ChoiceChargeTypeV2 {
    if (tipo === 'media' || tipo === EnumTipoDeCobrancaDeAdicional.MEDIA) return 'media';
    if (tipo === 'maior' || tipo === EnumTipoDeCobrancaDeAdicional.MAIOR) return 'maior';
    return 'soma';
  }

  private descrevaGrupo(adicional: any): string {
    if (adicional?.tipo === 'escolha-simples') {
      return `Escolha 1 • ${(adicional.opcoesDisponiveis || []).length} opções`;
    }

    const max = adicional?.qtdMaxima || 0;
    return `Até ${max} • ${(adicional.opcoesDisponiveis || []).length} opções`;
  }

  private mapLibraryItem(item: any): LibraryGroupVM {
    const rawType = this.mapTipoGrupo(item);
    const sharedCount = item?.sharedCount || item?.qtdeProdutos || item?.qtdProdutos || item?.usoEmProdutos || 0;
    const optionsCount = item?.opcoesDisponiveis?.length || item?.opcoes?.length || 0;
    const productId = item?.produto?.id || item?.objeto?.id || item?.item?.id || item?.produtoOrigem?.id || null;
    return {
      id: item.id || null,
      name: item.nome || 'Grupo',
      type: rawType,
      multiplicaQuantidadeProduto: item?.multiplicaQuantidadeProduto !== false,
      sharedCount,
      usageLabel: `${optionsCount} opções • usado em ${sharedCount} produtos`,
      productId,
      optionsCount,
      filters: ['Todos', sharedCount > 0 ? 'Mais usados' : 'Recentes'],
      raw: item
    };
  }

  private async carregueProdutosDaBiblioteca(catalogo: any, texto: string): Promise<any[]> {
    const cacheKey = Number(catalogo.id);
    const cached = this.libraryProductsCache.get(cacheKey);
    let products = cached || [];

    if (!products.length) {
      const [ativosResp, inativosResp] = await Promise.all([
        this.produtoService.liste(catalogo.id, true, 0, 400),
        this.produtoService.liste(catalogo.id, false, 0, 400)
      ]);

      const seen = new Map<number, any>();
      (ativosResp?.produtos || []).forEach((produto: any) => seen.set(produto.id, produto));
      (inativosResp?.produtos || []).forEach((produto: any) => {
        if (!seen.has(produto.id)) {
          seen.set(produto.id, produto);
        }
      });
      products = Array.from(seen.values());
      this.libraryProductsCache.set(cacheKey, products);
    }

    if (!texto) {
      return products;
    }

    try {
      const autocompleteProducts = await this.produtoService.obtenhaProdutosAC(catalogo, texto);
      const merged = new Map<number, any>();
      products.forEach((produto: any) => merged.set(produto.id, produto));
      (autocompleteProducts || []).forEach((produto: any) => {
        if (produto?.id && !merged.has(produto.id)) {
          merged.set(produto.id, produto);
        }
      });
      return Array.from(merged.values());
    } catch (error) {
      return products;
    }
  }

  async carregueNomesProdutosDoAdicional(catalogo: any, adicionalId: number | string): Promise<string[]> {
    if (!catalogo?.id || !adicionalId) return [];
    const products = await this.carregueProdutosDaBiblioteca(catalogo, '');
    const names: string[] = [];
    const id = String(adicionalId);
    products.forEach((produto: any) => {
      (produto?.camposAdicionais || []).forEach((adicional: any) => {
        if (String(adicional?.id) === id && produto?.nome && !names.includes(produto.nome)) {
          names.push(produto.nome);
        }
      });
    });
    return names;
  }

  private mapLibraryItemFromProduct(adicional: any, produto: any): LibraryGroupVM {
    const sharedCount = Number(adicional?.sharedCount || adicional?.qtdeProdutos || adicional?.qtdProdutos || adicional?.usoEmProdutos || 1) || 1;
    const options = (adicional?.opcoesDisponiveis || []).filter((opcao: any) => !opcao?.removida);
    const description = this.descrevaGrupo(adicional);
    const raw = {
      ...adicional,
      sharedCount,
      qtdeProdutos: sharedCount,
      qtdProdutos: sharedCount,
      produtoOrigem: produto ? { id: produto.id, nome: produto.nome, tipo: produto.tipo || 'normal' } : null
    };

    return {
      id: adicional.id,
      name: adicional.nome || 'Grupo',
      type: this.mapTipoGrupo(adicional),
      multiplicaQuantidadeProduto: adicional?.multiplicaQuantidadeProduto !== false,
      sharedCount,
      shared: sharedCount > 1 || !!adicional?.compartilhado,
      usageLabel: `${description} • ${sharedCount} produto${sharedCount > 1 ? 's' : ''}`,
      productId: produto?.id || null,
      sourceProductId: produto?.id || null,
      sourceProductName: produto?.nome || null,
      sourceProductType: (produto?.tipo || 'normal') as ProductTypeV2,
      optionsCount: options.length,
      filters: ['Todos', sharedCount > 1 ? 'Mais usados' : 'Recentes'],
      required: !!adicional?.obrigatorio,
      min: adicional?.qtdMinima || (adicional?.obrigatorio ? 1 : 0),
      max: adicional?.tipo === 'escolha-simples' ? 1 : (adicional?.qtdMaxima || 1),
      chargeType: this.mapTipoCobranca(adicional?.tipoDeCobranca),
      description,
      previewOptions: options.slice(0, 3).map((option: any) => option?.nome).filter((name: string) => !!name),
      helperText: sharedCount > 1
        ? 'Compartilhado — editar afeta todos'
        : 'Vincule para reaproveitar ou duplique para criar uma cópia',
      sharedProductNames: produto?.nome ? [produto.nome] : [],
      raw
    };
  }

  private mapGrupoParaLegacy(grupo: ChoiceGroupVM): any {
    const raw = grupo.raw ? Object.assign({}, grupo.raw) : {};
    let adicional: any;

    if (grupo.type === 'single') {
      adicional = new AdicionalDeProdutoEscolhaSimples(grupo.name, grupo.required, []);
      Object.assign(adicional, raw);
      adicional.tipo = 'escolha-simples';
      delete adicional.qtdMinima;
      delete adicional.qtdMaxima;
      delete adicional.podeRepetirItem;
      delete adicional.tipoDeCobranca;
    } else {
      adicional = new AdicionalDeProdutoMultiplaEscolha(
        grupo.name,
        grupo.required,
        [],
        grupo.min,
        grupo.max,
        grupo.type === 'multipleRepeat',
        this.mapTipoCobrancaLegacy(grupo.chargeType)
      );
      Object.assign(adicional, raw);
      adicional.tipo = 'multipla-escolha';
    }

    adicional.nome = grupo.name;
    adicional.obrigatorio = grupo.required;
    adicional.ordem = grupo.order;
    adicional.compartilhado = grupo.shared;
    adicional.multiplicaQuantidadeProduto = grupo.multiplicaQuantidadeProduto !== false;

    if (adicional.tipo === 'multipla-escolha') {
      adicional.qtdMinima = grupo.min;
      adicional.qtdMaxima = grupo.max;
      adicional.podeRepetirItem = grupo.type === 'multipleRepeat';
      adicional.tipoDeCobranca = this.mapTipoCobrancaLegacy(grupo.chargeType);
    }

    const visibleOptions = (grupo.options || []).map((opcao) => this.mapOpcaoParaLegacy(opcao));
    const removedOptions = (grupo.raw?.opcoesDisponiveis || [])
      .filter((opcao: any) => !!opcao?.removida)
      .map((opcao: any) => Object.assign({}, opcao))
      .filter((opcao: any) => !visibleOptions.some((current: any) => current.id != null && current.id === opcao.id));

    adicional.opcoesDisponiveis = [...visibleOptions, ...removedOptions];
    return adicional;
  }

  private mapOpcaoParaLegacy(opcao: ChoiceOptionVM): any {
    const raw = opcao.raw ? Object.assign({}, opcao.raw) : new OpcaoDeAdicionalDeProduto(opcao.name, opcao.price, opcao.active);
    raw.nome = opcao.name;
    raw.descricao = opcao.description || '';
    raw.linkImagem = opcao.linkImagem || null;
    raw.qtdeMaxima = opcao.qtdeMaxima ?? null;
    raw.valor = opcao.raw?.valor ?? opcao.price;
    raw.disponivel = opcao.raw?.disponivel ?? opcao.active;

    if (opcao.raw?.valorNaEmpresa != null) raw.valorNaEmpresa = opcao.raw.valorNaEmpresa;
    if (opcao.raw?.disponibilidadeNaEmpresa != null) raw.disponibilidadeNaEmpresa = opcao.raw.disponibilidadeNaEmpresa;
    if (opcao.raw?.opcaoNaEmpresa) {
      raw.opcaoNaEmpresa = Object.assign({}, opcao.raw.opcaoNaEmpresa);
    }

    if (opcao.raw?.nova) raw.nova = true;
    if (opcao.raw?.atualizar) raw.atualizar = true;
    if (opcao.raw?.removida) raw.removida = true;
    return raw;
  }

  private mapTipoCobrancaLegacy(tipo: ChoiceChargeTypeV2): EnumTipoDeCobrancaDeAdicional {
    if (tipo === 'media') {
      return EnumTipoDeCobrancaDeAdicional.MEDIA;
    }

    if (tipo === 'maior') {
      return EnumTipoDeCobrancaDeAdicional.MAIOR;
    }

    return EnumTipoDeCobrancaDeAdicional.SOMA;
  }

  private getBasePrice(produto: any): number {
    const startingPrice = this.getStartingPrice(produto);
    if (startingPrice != null) {
      return startingPrice;
    }

    const priceState = this.getEffectivePriceState(produto);
    if (priceState.effective != null) {
      return priceState.effective;
    }

    if (produto?.tamanhos?.length) {
      return Math.min(...produto.tamanhos.map((tamanho: any) => this.getBasePrice(tamanho)));
    }
    return 0;
  }

  private hasStartingPrice(produto: any): boolean {
    return this.toNullableNumber(produto?.valorMinimo) != null;
  }

  private getStartingPrice(produto: any): number | null {
    const valorMinimo = this.toNullableNumber(produto?.valorMinimo);
    if (valorMinimo == null) {
      return null;
    }

    const priceState = this.getEffectivePriceState(produto);
    const priceReference = priceState.companyBase ?? priceState.globalBase;
    const promoReference = priceState.companyPromo ?? priceState.globalPromo;

    if (promoReference != null && priceReference != null) {
      return Number((promoReference + (valorMinimo - priceReference)).toFixed(2));
    }

    return valorMinimo;
  }

  private getStatus(produto: any): ProductStatusV2 {
    const disponibilidade = this.getDisponibilidade(produto);
    return disponibilidade === 2 ? 'draft' : 'active';
  }

  private getGroupCount(produto: any): number {
    const base = produto?.camposAdicionais?.length || 0;
    return produto?.tipo === 'pizza' && produto?.tamanhos?.length ? base + 1 : base;
  }

  private resolvePlaceholder(produto: any): string {
    if (produto?.tipo === 'pizza') {
      return '🍕';
    }

    if (produto?.tipo === 'brindefidelidade' || produto?.ehBrinde) {
      return '🎁';
    }

    const nomeCategoria = String(produto?.categoria?.nome || '').toLowerCase();

    if (nomeCategoria.includes('acai') || nomeCategoria.includes('aça')) return '🍇';
    if (nomeCategoria.includes('burger') || nomeCategoria.includes('hamb')) return '🍔';
    if (nomeCategoria.includes('pizza')) return '🍕';
    if (nomeCategoria.includes('bebida') || nomeCategoria.includes('suco') || nomeCategoria.includes('refri')) return '🥤';
    if (nomeCategoria.includes('combo')) return '🎉';

    return '🍽️';
  }

  salveImagensProduto(empresa: any, produto: any): Promise<any> {
    return this.produtoService.salveImagensProduto(empresa, produto);
  }

  private resolveImageCandidates(produto: any): string[] {
    const linkImagem = produto?.imagens?.[0]?.linkImagem
      || produto?.linkImagem
      || produto?.imagem
      || null;

    if (!linkImagem) {
      return [];
    }

    if (/^https?:\/\//i.test(linkImagem)) {
      return [linkImagem];
    }

    const normalized = String(linkImagem).replace(/^\/+/, '');
    const localPath = `/images/empresa/${normalized}`;
    const absolutePath = `https://promokit.com.br/images/empresa/${normalized}`;

    return Array.from(new Set([localPath, absolutePath]));
  }

  private getEffectivePriceState(produto: any): {
    companyBase: number | null;
    companyPromo: number | null;
    globalBase: number | null;
    globalPromo: number | null;
    effective: number | null;
  } {
    const produtoNaEmpresa = produto?.produtoNaEmpresa || {};
    const companyBase = this.toNullableNumber(produtoNaEmpresa.preco ?? produto?.precoNaEmpresa);
    const companyPromo = this.toNullableNumber(produtoNaEmpresa.novoPreco ?? produto?.novoPrecoNaEmpresa);
    const globalBase = this.toNullableNumber(produto?.preco);
    const globalPromo = this.toNullableNumber(produto?.novoPreco);

    return {
      companyBase,
      companyPromo,
      globalBase,
      globalPromo,
      effective: companyPromo ?? globalPromo ?? companyBase ?? globalBase
    };
  }

  private resolveEffectivePriceTarget(produto: any): 'company-promo' | 'global-promo' | 'company-base' | 'global-base' {
    const priceState = this.getEffectivePriceState(produto);

    if (priceState.companyPromo != null) return 'company-promo';
    if (priceState.globalPromo != null) return 'global-promo';
    if (this.shouldUseCompanyPrice(produto)) return 'company-base';
    return 'global-base';
  }

  private getDisponibilidade(produto: any): number | null {
    if (this.shouldUseCompanyAvailability(produto)) {
      const companyAvailability = produto?.produtoNaEmpresa?.disponibilidade ?? produto?.disponibilidadeNaEmpresa;
      if (companyAvailability != null) {
        return companyAvailability;
      }
    }

    return produto?.disponibilidade ?? null;
  }

  private getOptionPrice(produto: any, opcao: any): number {
    if (this.shouldUseCompanyOptionPrice(produto, opcao)) {
      return this.toNullableNumber(opcao?.opcaoNaEmpresa?.valor ?? opcao?.valorNaEmpresa ?? opcao?.valor) ?? 0;
    }

    return this.toNullableNumber(opcao?.valor) ?? 0;
  }

  private getOptionAvailability(produto: any, opcao: any): boolean {
    if (this.shouldUseCompanyOptionAvailability(produto, opcao)) {
      const companyAvailability = opcao?.opcaoNaEmpresa?.disponivel ?? opcao?.disponibilidadeNaEmpresa;
      if (companyAvailability != null) {
        return companyAvailability !== false;
      }
    }

    return opcao?.disponivel !== false;
  }

  private getDestaque(produto: any): boolean {
    if (this.shouldUseCompanyScopedDestaque(produto)) {
      const destaqueNaEmpresa = produto?.produtoNaEmpresa?.destaque ?? produto?.destaqueNaEmpresa;
      if (destaqueNaEmpresa != null) {
        return !!destaqueNaEmpresa;
      }
    }

    return !!produto?.destaque;
  }

  private shouldUseCompanyPrice(produto: any): boolean {
    return !!(
      produto?.catalogo?.precoPorEmpresa
      || produto?.produtoNaEmpresa
      || produto?.precoNaEmpresa != null
      || produto?.novoPrecoNaEmpresa != null
    );
  }

  private shouldUseCompanyAvailability(produto: any): boolean {
    return !!(
      produto?.catalogo?.disponibilidadePorEmpresa
      || produto?.produtoNaEmpresa?.disponibilidade != null
      || produto?.disponibilidadeNaEmpresa != null
    );
  }

  private shouldUseCompanyScopedDestaque(produto: any): boolean {
    return !!(
      this.shouldUseCompanyPrice(produto)
      && (
        produto?.produtoNaEmpresa
        || produto?.precoNaEmpresa != null
        || produto?.novoPrecoNaEmpresa != null
        || produto?.destaqueNaEmpresa != null
      )
    );
  }

  private shouldUseCompanyOptionPrice(produto: any, opcao: any): boolean {
    return !!(
      produto?.catalogo?.precoPorEmpresa
      || opcao?.opcaoNaEmpresa?.valor != null
      || opcao?.valorNaEmpresa != null
    );
  }

  private shouldUseCompanyOptionAvailability(produto: any, opcao: any): boolean {
    return !!(
      produto?.catalogo?.disponibilidadePorEmpresa
      || opcao?.opcaoNaEmpresa?.disponivel != null
      || opcao?.disponibilidadeNaEmpresa != null
    );
  }

  private ensureProdutoNaEmpresa(produto: any): any {
    if (!produto.produtoNaEmpresa) {
      produto.produtoNaEmpresa = {};
    }

    return produto.produtoNaEmpresa;
  }

  private ensureOpcaoNaEmpresa(opcao: any): any {
    if (!opcao.opcaoNaEmpresa) {
      opcao.opcaoNaEmpresa = {};
    }

    opcao.opcaoNaEmpresa.opcao = opcao;
    return opcao.opcaoNaEmpresa;
  }

  apliqueEstadoOpcao(produto: any, opcao: any, state: { price: number; active: boolean }): void {
    const price = Number(state.price ?? 0);
    const active = state.active !== false;

    if (this.shouldUseCompanyOptionPrice(produto, opcao)) {
      const opcaoNaEmpresa = this.ensureOpcaoNaEmpresa(opcao);
      opcaoNaEmpresa.valor = price;
      opcao.valorNaEmpresa = price;

      if (opcao.id == null || opcao.valor == null) {
        opcao.valor = price;
      }
    } else {
      delete opcao.valorNaEmpresa;
      opcao.valor = price;
    }

    if (this.shouldUseCompanyOptionAvailability(produto, opcao)) {
      const opcaoNaEmpresa = this.ensureOpcaoNaEmpresa(opcao);
      opcaoNaEmpresa.disponivel = active;
      opcao.disponibilidadeNaEmpresa = active;

      if (opcao.id == null || opcao.disponivel == null) {
        opcao.disponivel = active;
      }
    } else {
      delete opcao.disponibilidadeNaEmpresa;
      opcao.disponivel = active;
    }
  }

  private mapFiscal(produto: any): any {
    const config = produto?.configuracaoFiscal || {};
    return {
      origem: config.origem || null,
      cest: config.cest || '',
      ncm: config.ncm || '',
      gtin: config.gtin || '',
      unidade: config.unidade || '',
      tipoCalculoIPI: config.tipoCalculoIPI || null,
      aliquotaIPI: config.aliquotaIPI ?? null,
      valorPorUnidadeIPI: config.valorPorUnidadeIPI ?? null,
      tipoDeTributacaoIPI: config.tipoDeTributacaoIPI || null,
      seloDeControleIPI: config.seloDeControleIPI || null,
      producaoPropria: !!config.producaoPropria,
      ipiCompoeBaseCalculoICMS: !!config.ipiCompoeBaseCalculoICMS,
      aliquotaAtributosAproximados: config.aliquotaAtributosAproximados ?? null,
      informacoesAdicionais: config.informacoesAdicionais || ''
    };
  }

  private toNullableNumber(value: any): number | null {
    if (value == null || value === '') return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  // ========== MÉTODOS DE ORDENAÇÃO ==========

  async reordenarProduto(
    catalogoId: number,
    produtoId: number,
    novaPosicao: number
  ): Promise<any> {
    return this.http.put(
      `/api/v2/produtos/${produtoId}/reordenar`,
      {
        catalogo: catalogoId,
        posicao: novaPosicao
      }
    ).toPromise();
  }

  async reordenarMultiplosProdutos(
    empresaId: number,
    ordenacao: Array<{ id: number; ordem: number }>
  ): Promise<any> {
    return this.http.put(
      `/produtos/ordens`,
      {
        eid: empresaId,
        produtos: ordenacao
      }
    ).toPromise();
  }

  async ordenarCategoriaPorNome(
    catalogoId: number,
    categoriaId: number,
    direcao: 'asc' | 'desc' = 'asc'
  ): Promise<any> {
    return this.http.post(
      `/api/v2/categorias/ordenar-alfabetico`,
      {
        catalogo: catalogoId,
        categoria: categoriaId,
        direcao
      }
    ).toPromise();
  }

  async salvarPreferenciaOrdenacao(
    catalogoId: number,
    campo: string,
    direcao: string
  ): Promise<any> {
    return this.http.put(
      `/api/v2/catalogos/${catalogoId}/preferencia-ordenacao`,
      {
        campo,
        direcao
      }
    ).toPromise();
  }
}
